import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import AppProvider from './AppProvider';
import Sidebar from './components/Sidebar';
import PricingModal from './components/PricingModal';
import LifetimeBanner from './components/LifetimeBanner';
import FeedbackModal from './components/FeedbackModal';
import BrandLogo from '@/components/brand/BrandLogo';
import { IconMenu } from './components/Icons';
import { useMemberApp } from './AppProvider';
import { CRISIS } from './prompts';
import './app-shell.css';

function ShellInner() {
  const { setSidebarOpen, loading, setFeedbackOpen } = useMemberApp();
  const [crisisOpen, setCrisisOpen] = useState(false);

  return (
    <div className="by-app">
      <Sidebar />
      <main className="by-main">
        {/* Global Premium Utility Header - Thin, translucent, Apple-style */}
        <header className="hidden md:flex items-center justify-between px-8 py-3.5 bg-[#f6f4ef]/80 backdrop-blur-md border-b border-[#e6e2d8] sticky top-0 z-40 select-none">
          {/* Breadcrumb / Patient Area indicator */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-extrabold uppercase tracking-wider text-[#6d6a63] bg-[#ece8df]/60 px-2.5 py-1 rounded border border-[#e6e2d8]">
              Oncology Support
            </span>
          </div>

          {/* Quick Actions Flex row */}
          <div className="flex items-center gap-3">
            {/* SOS / Crisis button */}
            <button
              type="button"
              onClick={() => setCrisisOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#b42318]/5 hover:bg-[#b42318]/10 border border-[#b42318]/20 text-[#b42318] text-xs font-extrabold rounded-full transition shadow-sm cursor-pointer"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#b42318] animate-pulse"></span>
              SOS Crisis Helpline
            </button>

            {/* Bookmarks Quick Link */}
            <a
              href="/records?tab=bookmarks"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-[#ece8df]/40 border border-[#e6e2d8] text-[#161616] text-xs font-extrabold rounded-full transition shadow-sm cursor-pointer text-decoration-none"
            >
              My Bookmarks
            </a>

            {/* Feedback button */}
            <button
              type="button"
              onClick={() => setFeedbackOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0f3e17]/5 hover:bg-[#0f3e17]/10 border border-[#0f3e17]/10 text-[#0f3e17] text-xs font-extrabold rounded-full transition shadow-sm cursor-pointer"
            >
              Feedback
            </button>
          </div>
        </header>

        <div className="by-mobile-bar">
          <button type="button" className="by-icon-btn" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
            <IconMenu />
          </button>
          <BrandLogo size="sm" className="by-mobile-brand" />
          <span />
        </div>
        {loading ? (
          <div className="by-empty" style={{ border: 0, background: 'transparent' }}>
            <div className="by-thinking" aria-label="Loading"><span /><span /><span /></div>
          </div>
        ) : (
          <Outlet />
        )}
      </main>
      <LifetimeBanner />
      <PricingModal />
      <FeedbackModal />

      {/* SOS CRISIS SUPPORT MODAL */}
      {crisisOpen && (
        <div className="fixed inset-0 bg-[#161616]/65 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-3xl max-w-2xl w-full border border-[#e6e2d8] shadow-2xl p-8 space-y-6 max-h-[85vh] overflow-y-auto animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center border-b border-[#e6e2d8] pb-4">
              <div className="space-y-1">
                <span className="text-[10px] uppercase font-extrabold text-[#b42318] tracking-widest block">24/7 Clinical Emergency Assistance</span>
                <h2 className="text-2xl font-extrabold text-[#161616] tracking-tight font-serif">
                  Crisis Resources
                </h2>
              </div>
              <button
                onClick={() => setCrisisOpen(false)}
                className="w-8 h-8 rounded-full bg-[#ece8df]/40 hover:bg-[#ece8df] flex items-center justify-center transition text-[#6d6a63] hover:text-[#161616] cursor-pointer border-0"
              >
                ✕
              </button>
            </div>

            <p className="text-sm text-[#6d6a63] leading-relaxed font-semibold">
              If you are in distress or need immediate clinical attention, please contact these emergency lines first. We are here to support you at every step of your journey.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {CRISIS.map((item) => (
                <div className="border border-[#e6e2d8] p-5 rounded-2xl bg-[#fdfdfc] hover:border-[#b42318]/30 transition-all flex flex-col justify-between space-y-4 text-left" key={item.title}>
                  <div className="space-y-2">
                    <span className="text-[9px] uppercase font-mono font-extrabold text-[#6d6a63] bg-[#ece8df]/40 px-2 py-0.5 rounded border border-[#e6e2d8]">
                      {item.meta}
                    </span>
                    <h3 className="text-sm font-extrabold text-[#161616] font-serif">{item.title}</h3>
                    <p className="text-xs text-[#6d6a63] leading-relaxed font-medium">{item.body}</p>
                  </div>
                  <a
                    className="w-full py-2.5 bg-[#0f3e17] hover:bg-[#1a4a21] text-white text-center rounded-xl text-xs font-bold transition shadow-sm border border-[#0f3e17] text-decoration-none"
                    href={item.href}
                    target={item.href.startsWith('http') ? '_blank' : undefined}
                    rel="noreferrer"
                  >
                    {item.action}
                  </a>
                </div>
              ))}
            </div>

            <div className="border-t border-[#e6e2d8] pt-4 text-center">
              <button
                onClick={() => setCrisisOpen(false)}
                className="py-2.5 px-6 bg-[#ece8df]/40 hover:bg-[#ece8df] text-[#161616] text-xs font-extrabold rounded-full transition cursor-pointer border-0"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AppShell() {
  return (
    <AppProvider>
      <ShellInner />
    </AppProvider>
  );
}
